"use client";

import { signIn } from "next-auth/react";
import DeleteBooking from "@/components/shared/delete-booking";
import PageTitle from "@/components/shared/page-title";
import Whatsapp from "@/components/shared/whatsapp";
import { Button } from "@/components/ui/button";
import { TSettings } from "@/types/settings";
import { useRouter } from "next/navigation";
import { useState } from "react";
import { onAuthStateChanged, signInWithEmailAndPassword } from "firebase/auth";
import { firebaseAuth } from "@/lib/firebaseClient";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import isValidEmail from "@/lib/is-valid-email";
import { Loader2 } from "lucide-react";
import Link from "next/link";

export default function LoginContainer({ settings }: { settings: TSettings }) {
  const router = useRouter();

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [userErrors, setUserErrors] = useState({
    username: false,
    password: false,
  });

  const [error, setError] = useState("");
  const [isLoading, setIsloading] = useState(false);

  const login = async () => {
    setError("");
    setIsloading(true);

    try {
      const userCred = await signInWithEmailAndPassword(
        firebaseAuth,
        username.trim(),
        password
      );

      // Wait until Firebase auth state is definitely “in place”
      await new Promise<void>((resolve) => {
        const unsub = onAuthStateChanged(firebaseAuth, (u) => {
          if (u) {
            unsub();
            resolve();
          }
        });
      });

      const tokenResult = await userCred.user.getIdTokenResult(); // includes claims
      const token = tokenResult.token;

      // await signIn("credentials", {
      //   token,
      //   redirect: true,
      //   callbackUrl: "/user/calendar",
      // });

      // console.log('token', token.length)

      const callbackUrl = tokenResult.claims.role === 'admin' ? "/admin/calendar" : "/user/calendar"

      const res = await signIn("credentials", {
        token,
        redirect: false,
        callbackUrl,
      });


      // console.log("nextauth signIn result", res);
      if (res?.ok) {
        router.push(callbackUrl);
      } else {
        setError("Tu contraseña no es correcta.");
        setIsloading(false);
      }

      // location.href = tokenResult.claims.role === 'admin' ? "/admin/calendar" : "/user/calendar"
    } catch (e) {
      const error = (e as any).code;

      switch (error) {
        case "auth/invalid-credential":
        default:
          setError("Tu contraseña no es correcta.");
          break;
      }

      setIsloading(false);
    }
  };

  return (
    <div className="w-full">
      <DeleteBooking />
      <PageTitle title="Iniciar sesión" />

      <div className="pt-40">
        <Button
          variant={"outline"}
          className="mt-8"
          onClick={() => router.push("/")}
        >
          {"< "} Volver al calendario
        </Button>
      </div>

      <div className="border-1 radius-base p-8 mt-16 mb-16 w-full">
        <h2 className="w-full border-b-1 pb-1 mb-6 font-domine text-2xl font-bold mb-16">
          Iniciar sesión
        </h2>
        <div className="w-full mt-4 flex gap-4 md:gap-8 flex-col md:flex-row">
          <div className="flex-1">
            <Label
              className={
                "z-10 pl-1 relative transition-opacity duration-500 text-md -mb-1 " +
                ((username === "" || !isValidEmail(username)) &&
                userErrors.username
                  ? " text-secondary-text"
                  : "")
              }
              htmlFor="username"
            >
              Dirección de correo electrónico*
            </Label>
            <Input
              className="pl-1"
              type="email"
              name="username"
              value={username}
              onChange={(e) => {
                setUsername(e.target.value);
                setUserErrors({
                  ...userErrors,
                  username:
                    e.target.value === "" || !isValidEmail(e.target.value),
                });
              }}
              onBlur={() =>
                setUserErrors({
                  ...userErrors,
                  username: username === "" || !isValidEmail(username),
                })
              }
              onKeyUp={(e: React.KeyboardEvent<HTMLInputElement>) => {
                if (
                  e.key === "Enter" &&
                  !(
                    isLoading ||
                    username === "" ||
                    password === "" ||
                    userErrors.username ||
                    userErrors.password
                  )
                ) {
                  login();
                }
              }}
            />
            <div
              className={
                "mt-2 text-xs text-secondary-text transition-opacity duration-500 " +
                ((username === "" || !isValidEmail(username)) &&
                userErrors.username
                  ? "opacity-100"
                  : "opacity-0")
              }
            >
              Por favor, introduzca los datos correctamente para este campo
              (obligatorio).
            </div>
          </div>

          <div className="flex-1">
            <Label
              className={
                "z-10 pl-1 relative transition-opacity duration-500 text-md -mb-1 " +
                (password === "" && userErrors.password
                  ? " text-secondary-text"
                  : "")
              }
              htmlFor="password"
            >
              Contraseña*
            </Label>
            <Input
              className="pl-1"
              name="password"
              type="password"
              value={password || ""}
              onChange={(e) => setPassword(e.target.value)}
              onBlur={() =>
                setUserErrors({
                  ...userErrors,
                  password: password === "",
                })
              }
              onKeyUp={(e: React.KeyboardEvent<HTMLInputElement>) => {
                if (
                  e.key === "Enter" &&
                  !(
                    isLoading ||
                    username === "" ||
                    password === "" ||
                    userErrors.username ||
                    userErrors.password
                  )
                ) {
                  login();
                }
              }}
            />
            <div
              className={
                "mt-2 text-xs text-secondary-text transition-opacity duration-500 " +
                (password === "" && userErrors.password
                  ? "opacity-100"
                  : "opacity-0")
              }
            >
              Por favor, introduzca los datos correctamente para este campo
              (obligatorio).
            </div>
          </div>
        </div>
        <div className="flex justify-end w-full mt-14">
          <Button
            disabled={
              isLoading ||
              username === "" ||
              password === "" ||
              userErrors.username ||
              userErrors.password
            }
            onClick={() => login()}
          >
            {isLoading ? <Loader2 className="animate-spin" /> : <></>}
            Continuar
          </Button>
        </div>
        {error !== "" ? (
          <div className="w-full mt-4 pr-2 flex justify-end text-xs text-secondary-text">
            {error}
          </div>
        ) : (
          <></>
        )}
        <div className="w-full mt-12 pr-2 text-xs text-primary-text">
          ¿Ha perdido tu contraseña?
          <Link
            className="underline ml-1"
            title="Haga clic aquí para recuperar su cuenta."
            href="/user/recover-password"
          >
            Haga clic aquí para recuperar su cuenta.
          </Link>
        </div>
      </div>

      <div
        className={
          "fixed bottom-3 right-4 rounded-full z-99 animate-[bounce-once_1s_ease-out_3]"
        }
      >
        <Whatsapp />
      </div>
    </div>
  );
}
